import { useState } from "react";
import { Heart } from "lucide-react";
import { motion } from "framer-motion";
import { DonateModal } from "./DonateModal";

const InstagramIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true" focusable="false">
    <rect width="20" height="20" x="2" y="2" rx="5" ry="5"/>
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
    <line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/>
  </svg>
);

const LinkedinIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true" focusable="false">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
    <rect width="4" height="12" x="2" y="9"/>
    <circle cx="4" cy="4" r="2"/>
  </svg>
);

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
};

export const Footer = () => {
  const [donateOpen, setDonateOpen] = useState(false);

  const socialLinks = [
    { icon: InstagramIcon, href: "https://instagram.com/byte.sized.python", label: "Instagram" },
    { icon: LinkedinIcon, href: "https://www.linkedin.com/company/byte-sized-python/", label: "LinkedIn" }
  ];

  return (
    <>
      <DonateModal isOpen={donateOpen} onClose={() => setDonateOpen(false)} />

      <footer className="relative py-20 overflow-hidden border-t border-border">
        <div className="absolute inset-0 bg-gradient-to-b from-white via-white to-primary/10"></div>
        <div className="absolute inset-0 [mask-image:radial-gradient(ellipse_at_center,transparent_0%,white_75%)]" style={{ backgroundImage: 'var(--gradient-spotlight)' }}></div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid md:grid-cols-4 gap-12"
          >
            {/* Brand */}
            <motion.div variants={item} className="md:col-span-2 space-y-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary rounded-lg">
                  <img src="64x64 Blue.png" width={22} height={22} alt="Byte-Sized Python" />
                </div>
                <h3 className="text-2xl font-medium text-foreground">Byte-Sized Python</h3>
              </div>

              <p className="text-muted-foreground leading-relaxed max-w-md">
                Inspiring the next generation of coders through fun, accessible, and
                age-appropriate Python programming education.
              </p>

              {/* Donate Now button */}
              <button
                onClick={() => setDonateOpen(true)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-primary/80 active:scale-[0.98] transition-all duration-200 shadow-lg shadow-primary/25"
              >
                <Heart className="w-4 h-4" fill="currentColor" />
                Donate Now
              </button>
            </motion.div>

            {/* Contact */}
            <motion.div variants={item} className="space-y-6">
              <h4 className="text-lg font-medium text-foreground">Get In Touch</h4>
              <div className="flex space-x-2">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    target="_blank"
                    rel="noopener noreferrer"
                    href={social.href}
                    className="w-10 h-10 flex items-center justify-center bg-background/80 border border-foreground/10 hover:bg-primary/10 hover:text-primary rounded-full transition-colors duration-300 group"
                    aria-label={social.label}
                  >
                    <social.icon className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </a>
                ))}
              </div>
              <div className="space-y-3 text-muted-foreground">
                <a href="mailto:contact@bytesizedpython.org">contact@bytesizedpython.org</a>
                <br/>
                <a href="tel:9802360868">+1 (980) 236-0868</a>
              </div>
            </motion.div>
          </motion.div>

          {/* Bottom bar */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="mt-16 pt-8 border-t border-border"
          >
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-muted-foreground text-sm text-center md:text-left">
                &copy; {new Date().getFullYear()} Byte-Sized Python. All rights reserved. A 501(c)(3) nonprofit organization.
              </p>

              <p className="text-muted-foreground text-sm font-medium">
                Inspiring the next generation of coders.
              </p>
            </div>
          </motion.div>
        </div>
      </footer>
    </>
  );
};
